import React, { useState, useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Alert,
  AlertIcon,
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Checkbox,
  SimpleGrid,
  IconButton,
  HStack,
  ButtonGroup,
} from "@chakra-ui/react";
import PageLoader from "./components/PageLoader";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import api from "./api";

interface Token {
  name: string;
  symbol: string;
  assetSymbol: string;
  baseSymbol: string;
  decimals: number;
  address: string;
  isShortable: boolean;
  categories: string[];
  imageUrl: string;
  coingeckoUrl: string;
  explorerUrl: string;
  isV1Available: boolean;
  isPermitSupported: boolean;
  contractVersion: string;
  _id: string;
}

function Tokens() {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newToken, setNewToken] = useState<Partial<Token>>({
    name: "",
    symbol: "",
    assetSymbol: "",
    baseSymbol: "",
    decimals: 0,
    address: "",
    isShortable: false,
    categories: [],
    imageUrl: "",
    coingeckoUrl: "",
    explorerUrl: "",
    isV1Available: false,
    isPermitSupported: false,
    contractVersion: "1",
    _id: "",
  });
  const [isRankOpen, setIsRankOpen] = useState(false);
  const [rankTokenId, setRankTokenId] = useState<string | null>(null);
  const [newRank, setNewRank] = useState<number>(0);
  const [rankLoading, setRankLoading] = useState(false);

  const fetchTokens = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/api/admin/tokens`);
      setTokens(res?.data?.data.tokens || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Error loading tokens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);

  const handleAddOpen = () => {
    setIsEditing(false);
    setNewToken({
      name: "",
      symbol: "",
      assetSymbol: "",
      baseSymbol: "",
      decimals: 8,
      address: "",
      isShortable: false,
      categories: [],
      imageUrl: "",
      coingeckoUrl: "",
      explorerUrl: "",
      isV1Available: false,
      isPermitSupported: false,
      contractVersion: "1",
      _id: "",
    });
    setIsAddOpen(true);
  };

  const handleEdit = (token: Token) => {
    setIsEditing(true);
    setNewToken(token);
    setIsAddOpen(true);
  };

  const handleDelete = async (address: string) => {
    try {
      await api.delete(`/api/admin/tokens/${address}`);
      fetchTokens();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Error deleting token");
    }
  };

  const handleAddChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setNewToken({ ...newToken, [name]: checked });
    } else if (name === "categories") {
      setNewToken({ ...newToken, categories: value.split(",") });
    } else if (name === "decimals") {
      setNewToken({ ...newToken, decimals: Number(value) });
    } else {
      setNewToken({ ...newToken, [name]: value });
    }
  };

  const handleSave = async () => {
    try {
      if (isEditing) {
        await api.put(
          `/api/admin/tokens/${newToken?._id}`,
          newToken
        );
      } else {
        delete newToken._id;
        await api.post(`/api/admin/tokens`, newToken);
      }
      setIsAddOpen(false);
      fetchTokens();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          `Error ${isEditing ? "updating" : "adding"} token`
      );
    }
  };

  // Open rank modal and fetch current rank
  const handleRankOpen = (token: any) => {
    setRankTokenId(token?._id);
    setIsRankOpen(true);
    // setRankLoading(true);
  };

  // Update rank API call
  const handleRankSave = async () => {
    if (!rankTokenId) return;
    setRankLoading(true);
    try {
      await api.post(`/api/admin/tokens/update-rank`, {
        id: rankTokenId,
        newRank: newRank,
      });
      setIsRankOpen(false);
      fetchTokens();
      toast.success("Rank updated successfully!");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error updating rank");
    } finally {
      setRankLoading(false);
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Toaster position="top-right" />
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="xl" textAlign="center">
          Token Management
        </Heading>
        <Button
          colorScheme="green"
          onClick={handleAddOpen}
          alignSelf="flex-end"
        >
          Add Token
        </Button>
        {loading && <PageLoader message="Loading tokens..." />}

        <Box overflowX="auto">
          {error ? (
            <Alert status="error">
              <AlertIcon />
              {error}
            </Alert>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Rank</Th>
                  <Th>Name</Th>
                  <Th>Symbol</Th>
                  <Th>Asset Symbol</Th>
                  <Th>Base Symbol</Th>
                  <Th>Address</Th>
                  <Th>Decimals</Th>
                  <Th>Shortable</Th>
                  <Th>Categories</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tokens.map((token: any, idx) => (
                  <Tr key={token.address + idx}>
                    <Td>{token.rank}</Td>
                    <Td>{token.name}</Td>
                    <Td>{token.symbol}</Td>
                    <Td>{token.assetSymbol}</Td>
                    <Td>{token.baseSymbol}</Td>
                    <Td>
                      <a
                        href={token.explorerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {token.address.slice(0, 6)}...{token.address.slice(-4)}
                      </a>
                    </Td>
                    <Td>{token.decimals}</Td>
                    <Td>{token.isShortable ? "Yes" : "No"}</Td>
                    <Td>{token.categories.join(", ")}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <ButtonGroup>
                          <Button
                            size="sm"
                            onClick={() => {
                              setNewRank(token.rank || 0);
                              handleRankOpen(token);
                            }}
                          >
                            Update Rank
                          </Button>
                        </ButtonGroup>
                        <IconButton
                          aria-label="Edit token"
                          icon={<EditIcon />}
                          size="sm"
                          onClick={() => handleEdit(token)}
                        />
                        <IconButton
                          aria-label="Delete token"
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleDelete(token?._id)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>

      <Modal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? "Edit Token" : "Add New Token"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="70vh" overflowY="auto">
            <VStack spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input
                    name="name"
                    value={newToken.name || ""}
                    onChange={handleAddChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Symbol</FormLabel>
                  <Input
                    name="symbol"
                    value={newToken.symbol || ""}
                    onChange={handleAddChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Asset Symbol</FormLabel>
                  <Input
                    name="assetSymbol"
                    value={newToken.assetSymbol || ""}
                    onChange={handleAddChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Base Symbol</FormLabel>
                  <Input
                    name="baseSymbol"
                    value={newToken.baseSymbol || ""}
                    onChange={handleAddChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Address</FormLabel>
                  <Input
                    name="address"
                    value={newToken.address || ""}
                    onChange={handleAddChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Decimals</FormLabel>
                  <Input
                    name="decimals"
                    type="number"
                    value={newToken.decimals || 8}
                    onChange={handleAddChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Categories (comma separated)</FormLabel>
                  <Input
                    name="categories"
                    value={newToken.categories?.join(",") || ""}
                    onChange={handleAddChange}
                    placeholder="e.g., layer1,defi"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Image URL</FormLabel>
                  <Input
                    name="imageUrl"
                    value={newToken.imageUrl || ""}
                    onChange={handleAddChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Coingecko URL</FormLabel>
                  <Input
                    name="coingeckoUrl"
                    value={newToken.coingeckoUrl || ""}
                    onChange={handleAddChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Explorer URL</FormLabel>
                  <Input
                    name="explorerUrl"
                    value={newToken.explorerUrl || ""}
                    onChange={handleAddChange}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Shortable</FormLabel>
                  <Checkbox
                    name="isShortable"
                    isChecked={newToken.isShortable}
                    onChange={(e) => handleAddChange(e as any)}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">V1 Available</FormLabel>
                  <Checkbox
                    name="isV1Available"
                    isChecked={newToken.isV1Available}
                    onChange={(e) => handleAddChange(e as any)}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Permit Supported</FormLabel>
                  <Checkbox
                    name="isPermitSupported"
                    isChecked={newToken.isPermitSupported}
                    onChange={(e) => handleAddChange(e as any)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Contract Version</FormLabel>
                  <Input
                    name="contractVersion"
                    value={newToken.contractVersion || ""}
                    onChange={handleAddChange}
                  />
                </FormControl>
              </SimpleGrid>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" mr={3} onClick={handleSave}>
              {isEditing ? "Update" : "Save"}
            </Button>
            <Button onClick={() => setIsAddOpen(false)}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Update Rank Modal */}
      <Modal isOpen={isRankOpen} onClose={() => setIsRankOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Token Rank</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>New Rank</FormLabel>
              <Input
                type="number"
                value={newRank}
                onChange={(e) => setNewRank(Number(e.target.value))}
                placeholder="Enter new rank"
                disabled={rankLoading}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleRankSave}
              isLoading={rankLoading}
            >
              Save
            </Button>
            <Button onClick={() => setIsRankOpen(false)}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}

export default Tokens;
